<template>
  <v-row id="valkyrie-graph">
    <event-detail-modal
      v-model="detailOpen"
    />
    <div
      ref="pdfExport"
      class="export-wrapper"
    >
      <v-card
        class="mt-1"
        width="100%"
      >
        <graph-header
          :dates="dates"
          :refreshing="rotate"
          :range-selected="rangeSelected"
          data-html2canvas-ignore="true"
          @update-date="handleDateUpdate"
          @refresh-graph="refreshGraphData"
          @export-graph="exportPDF"
        />
        <zones-legend
          :zones-legend="zonesLegend"
          @toggle-zone="toggleZoneActive"
        />
        <div class="pb-5 px-5">
          <valkyrie-graph-legend ref="graphLegend" />
          <v-skeleton-loader
            v-if="!resolved"
            class="graph-loader"
            max-width="100%"
            height="300px"
            min-height="300px"
            type="image"
            loading
          />
          <graph
            v-else-if="resolved"
            :render-key="rerenderKey"
            :chart-model="chartModel"
            :chart-options="chartOptions"
            :tooltip-points="graphTooltipData"
            :timezone="propertyTimezone"
            height="300px"
            chart-name="valkyrieGraphChart"
          />
          <v-row
            justify="space-between"
            dense
          >
            <v-col class="ml-14">
              <span class="lower-date">{{ dates.startDate | formatAxesLimit }}</span>
            </v-col>
            <v-col class="text-right mr-9">
              <span class="lower-date">{{ dates.endDate | formatAxesLimit }}</span>
            </v-col>
          </v-row>
          <div v-if="occupancyEligible">
            <occupancy-graph-header />
            <v-skeleton-loader
              v-if="!resolved"
              class="graph-loader"
              max-width="100%"
              height="100px"
              min-height="100px"
              type="image"
              loading
            />
            <occupancy-graph
              v-if="resolved"
              height="100px"
              :max="maxOccupancyGraph"
              :timezone="propertyTimezone"
              :chart-model="occChartModel"
              :render-key="rerenderKey"
              :activity-zones="occupancyDataByAz.map(az => az.azName)"
            />
          </div>
          <template v-else-if="propertyIsHouse">
            <occupancy-graph-header />
            <div class="occupancy-graph-placeholder">
              <div>
                This feature is not supported by your sensor
                <div>
                  <a
                    class="caption-2"
                    style="text-align: center"
                    target="_blank"
                    href="https://help.noiseaware.io/hc/en-us/articles/5641387258003-CrowdControl-Support"
                  >View Sensor Requirements</a>
                </div>
              </div>
            </div>
          </template>
        </div>
      </v-card>
    </div>
  </v-row>
</template>

<script>
  import nonReactiveMixin from '@/helpers/non-reactive-mixin.js'
  import axios from 'axios'
  import { DateTime } from 'luxon'
  import Graph from '@/components/base/Graph.vue'
  import { defaultGraphOptions } from './base/Graph.vue'
  import ValkyrieGraphLegend from '@/components/base/ValkyrieGraphLegend.vue'
  import ZonesLegend from './ZonesLegend.vue'
  import GraphHeader from './GraphHeader.vue'
  import EventDetailModal from '@/components/events/EventDetailModal.vue'
  import exportPdfMixin from '@/helpers/export-pdf-mixin.js'
  import occupancyGraphMixin from '@/helpers/occupancy-graph-mixin.js'
  import { faCircle, faComment } from '@/assets/icons/free-solid.js'
  import { nearAlert } from '@/components/base/GraphEventTooltip.js'
  import { mapGetters } from 'vuex'
  import OccupancyGraphHeader from './OccupancyGraphHeader.vue'
  import OccupancyGraph from './OccupancyGraph.vue'
  import { OCCUPANCY } from '@/helpers/feature-constants.js'

  const colors = [
    '#25954A',
    '#CC6677',
    '#332288',
    '#0072B2',
    '#44AA99',
    '#882255',
    '#D55E00',
    '#E69F00',
  ]

  const seconds = 1000
  const minutes = 60 * seconds
  const axesLimitsString = 'LLL d, yyyy'

  export default {
    name: 'ValkyrieGraph',
    components: {
      Graph,
      ValkyrieGraphLegend,
      ZonesLegend,
      GraphHeader,
      OccupancyGraph,
      OccupancyGraphHeader,
      EventDetailModal,
    },
    filters: {
      formatAxesLimit (date) {
        return DateTime.fromJSDate(date).toFormat(axesLimitsString)
      },
    },
    mixins: [nonReactiveMixin, exportPdfMixin, occupancyGraphMixin],
    props: {
      name: {
        type: String,
        default: 'Noise Risk Score',
      },
      propertyId: String,
      propertyType: String,
    },
    data: () => ({
      now: Date.now(),
      clock: null,
      dates: {
        startDate: new Date(Date.now() - (30 * minutes)),
        endDate: new Date(),
      },
      activeZones: {},
      resolved: false,
      rotate: false,
      rerenderKey: 0,
      rangeSelected: {},
      graphData: {
        xValue: [],
        series: [],
      },
      detailOpen: false,
      occupancyGraphData: {},
      occupancyEligible: false,
    }),
    computed: {
      ...mapGetters('Properties', [
        'noiseEventTimestamps',
        'disconnectEventTimestamps',
        'graphTooltipData',
        'graphEventAnnotations',
        'graphAlerts',
        'propertyDetails',
      ]),
      ...mapGetters({
        zones: 'ActivityZones/zones',
      }),
      graphEventAnnotationsWithClickHandler () {
        return this.graphEventAnnotations.map(({ event, ...annotation }) => ({
          ...annotation,
          onClick: () => {
            this.$store.commit('Events/updateSelectedEvent', event)
            this.detailOpen = true
          },
        }))
      },
      iconSVG () {
        const svgString = `
        <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 512 512" 
            height="23px"
            width="23px"
          >
            <path fill="black" d="${faCircle.icon[4]}"></path>
            <g transform="scale(0.58, 0.58) translate(172, 172)">
              <path fill="white" d="${faComment.icon[4]}"></path>
            </g>
        </svg>
        `
        const img = new Image(23, 23)
        img.src = `data:image/svg+xml;utf8,${svgString}`
        return img
      },
      propertyIsHouse () {
        return this.propertyDetails.propertyType === 'House' || this.propertyDetails.propertyType === 'House Sensitive'
      },
      chartModel () {
        const chartModel = {
          labels: this.graphData.xValue,
          datasets: this.graphData.series.map((set) => {
            if (set.type === 'nrs') return this.getNRSStyleProperties(set, this.graphData.xValue, this.graphTooltipData.alerts)
            else if (set.type === 'threshold') return this.getThresholdStyleProperties(set)
          }),
        }

        if (this.graphAlerts?.length) {
          chartModel.datasets.push(this.getAlertDataset(this.graphData.xValue, this.graphTooltipData.alerts))
        }

        return chartModel
      },
      chartOptions () {
        return {
          ...defaultGraphOptions,
          annotation: {
            events: ['mouseenter', 'mouseleave', 'click', 'mouseup'],
            annotations: this.graphEventAnnotationsWithClickHandler,
          },
        }
      },
      zoneColors () {
        return this.zones.reduce((acc, curr, i) => ({ [curr.id]: colors[i % colors.length], ...acc }), {})
      },
      zonesLegend () {
        return this.zones.map((zone) => {
          return { ...zone, active: this.activeZones[zone.id], color: this.zoneColors[zone.id] }
        })
      },
      propertyTimezone () {
        return this.propertyDetails.timezone
      },
    },
    async created () {
      this.updateNow()

      await this.$store.dispatch('ActivityZones/fetchZones', { id: this.$route.params.id })
      this.initializeActiveZones()
      if (this.zones.length) {
        await this.fetchOccupancyEligibility()
        await this.fetchAndRenderGraph(this.dates.startDate.toISOString(), this.dates.endDate.toISOString())
      }
    },
    methods: {
      async fetchOccupancyEligibility () {
        const response = await axios.get(`${this.$store.state.baseURL}/eligibility/property/${this.propertyId}/feature/${OCCUPANCY}`)
        this.occupancyEligible = response.data.data?.isEligible
      },
      fetchAndRenderGraph (startDateISOString, endDateISOString) {
        return this.getGraphData(startDateISOString, endDateISOString)
          .then(({ nrs, occupancy }) => {
            this.markDataNonReactive(nrs)
            this.graphData = nrs
            if (occupancy) this.occupancyGraphData = occupancy
            this.resolved = true
          })
          .then(() => this.rerenderGraph())
      },
      async getGraphData (startDate, endDate) {
        this.$store.dispatch('Properties/fetchGraphEvents', { startTime: startDate, endTime: endDate, id: this.propertyId })
        this.$store.dispatch('Properties/fetchGraphAlerts', { startTime: startDate, endTime: endDate, id: this.propertyId })
        const response = await axios.get(`${this.$store.state.baseURL}/properties/graph/${this.propertyId}/${startDate}/${endDate}`, {})
        if (this.occupancyEligible) {
          const occupancyResponse = await this.getOccupancyGraphData(startDate, endDate)
          return { nrs: response.data.data, occupancy: occupancyResponse.data.data }
        }
        return { nrs: response.data.data }
      },
      initializeActiveZones () {
        this.activeZones = this.zones.reduce((acc, curr) => ({ [curr.id]: true, ...acc }), {})
      },
      toggleZoneActive (toggledZoneId) {
        this.activeZones[toggledZoneId] = !this.activeZones[toggledZoneId]
        this.rerenderGraph()
      },
      handleDateUpdate ({ dates, diff }, range) {
        this.dates = dates
        if (range) this.rangeSelected = range
        this.refreshGraphData()
      },
      updateNow () {
        return setTimeout(() => {
          this.now = Date.now()
          clearTimeout(this.clock)
          this.clock = this.updateNow()
        }, 30 * seconds)
      },
      refreshDatesFromRangeSelected () {
        return {
          endDate: new Date(this.now),
          startDate: new Date(this.now - this.rangeSelected.delta),
        }
      },
      refreshGraphData () {
        this.rotate = true
        if (this.rangeSelected.value) this.dates = this.refreshDatesFromRangeSelected()
        this.fetchAndRenderGraph(this.dates.startDate.toISOString(), this.dates.endDate.toISOString())
          .then(() => { this.rotate = false })
      },
      getThresholdStyleProperties (dataset) {
        return {
          id: dataset.zoneId,
          label: dataset.type,
          borderColor: this.zoneColors[dataset.zoneId],
          hidden: !this.activeZones[dataset.zoneId],
          data: dataset.yValue,
          fill: false,
          lineTension: 0,
          borderWidth: 3,
          borderDash: [8, 4],
          pointRadius: 0,
          pointHitRadius: 0,
          pointHoverRadius: 0,
        }
      },
      getNRSStyleProperties (dataset, xValues, alertPoints) {
        return {
          id: dataset.zoneId,
          label: dataset.type,
          borderColor: this.zoneColors[dataset.zoneId],
          hidden: !this.activeZones[dataset.zoneId],
          data: dataset.yValue.map(nrs => nrs === -1 ? null : nrs),
          fill: false,
          lineTension: 0,
          cubicInterpolationMode: 'monotone',
          pointRadius: 0,
          pointHoverRadius: 0,
        }
      },
      getAlertDataset (xValues, alertPoints) {
        const unmarkedAlertPoints = JSON.parse(JSON.stringify(alertPoints)) // copy w/o reactivity

        const alertData = xValues.map(timestamp => {
          const utcTimestamp = DateTime.fromISO(timestamp).toUTC().toISO()

          for (let i = unmarkedAlertPoints.length - 1; i >= 0; i--) {
            const alert = unmarkedAlertPoints[i]

            if (nearAlert(utcTimestamp, alert.timestamp, 1)) {
              unmarkedAlertPoints.splice(i, 1)
              return 3
            }
          }
        })

        return {
          data: alertData,
          pointStyle: this.iconSVG,
          pointHitRadius: 5,
        }
      },
      rerenderGraph () {
        this.rerenderKey++
      },
      getPointColor (dataset, context) {
        return this.hasNoiseEventAtPoint(dataset, context) ? 'rgba(255,0,0,1)' : this.zoneColors[dataset.zoneId]
      },
      getPointRadius (dataset, context) {
        return this.hasNoiseEventAtPoint(dataset, context) ? 5 : 0
      },
      hasNoiseEventAtPoint (dataset, context) {
        const zoneEventTimestamps = this.noiseEventTimestamps[dataset.zoneId]
        const datasetTimestamp = this.graph.xValue.find((_, i) => context.dataIndex === i)
        return (zoneEventTimestamps && zoneEventTimestamps.includes(datasetTimestamp))
      },
      hasDisconnectEventAtPoint (dataset, context) {
        const zoneEventTimestamps = this.disconnectEventTimestamps[dataset.zoneId]
        const datasetTimestamp = this.graph.xValue.find((_, i) => context.dataIndex === i)
        return (zoneEventTimestamps && zoneEventTimestamps.includes(datasetTimestamp))
      },
    },
  }
</script>
<style lang="scss">
#valkyrie-graph {
  margin: 0 32px;
  .export-wrapper {
    width: 100%;
  }
  .graph-loader {
    .v-skeleton-loader__image {
      min-height:100%;
    }
  }
  .lower-date {
    color: $primary;
    font-family: "Degular", sans-serif;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.81px;
    line-height: 18px;
  }
  .occupancy-graph-placeholder {
    color: #a8a8a8;
    background-color: $na-off-white;
    margin: auto;
    display: flex;
    flex-direction: column;
    height: 100px;
    width: 100%;
    font-family: Degular;
    font-size: 21px;
    font-weight: 400;
    line-height: 30px;
    letter-spacing: 0em;
    text-align: center;
    & > * {
      flex: none;
      margin: auto;
    }
  }
}
</style>
